import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingDispatch } from '../../context/OnboardingContext';
import { useAuth } from '../../hooks/useAuth';
import { api, ApiError } from '../../services/api';
import { useLanguage } from '../../i18n/LanguageContext';
import { TermsDocumentPublic, TermsSignerRole, TermsSigningContext } from '../../types';
import { useTermsDownload } from '../../hooks/useTermsDownload';
import TermsProgressHeader from '../../components/onboarding/terms/TermsProgressHeader';
import TermsTimelineRail from '../../components/onboarding/terms/TermsTimelineRail';
import TermsDocumentPanel from '../../components/onboarding/terms/TermsDocumentPanel';
import TermsFinalReview from '../../components/onboarding/terms/TermsFinalReview';
import TermsSignatureBlock, {
  isSignatureValid,
} from '../../components/onboarding/terms/TermsSignatureBlock';

const interpolate = (template: string, vars: Record<string, string | number>) =>
  Object.entries(vars).reduce(
    (acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v)),
    template,
  );

const sortDocs = (docs: TermsDocumentPublic[]) => [...docs].sort((a, b) => a.sortOrder - b.sortOrder);

/** Spec 071 — the error code of a finalize failure, wherever the backend put it. */
const apiErrorCodes = (err: unknown): string[] => {
  if (!(err instanceof ApiError)) return [];
  const codes = [err.code, err.errorCode];
  if (err.body && typeof err.body.error === 'string') codes.push(err.body.error);
  if (err.body && typeof err.body.code === 'string') codes.push(err.body.code);
  if (err.fieldErrors) {
    Object.entries(err.fieldErrors).forEach(([k, v]) => codes.push(k, String(v)));
  }
  return codes.filter((c): c is string => typeof c === 'string' && c.length > 0);
};

const hasCode = (err: unknown, code: string): boolean =>
  apiErrorCodes(err).includes(code) || (err instanceof Error && err.message.includes(code));

/** Spec 071 — `staleDocumentIds` of a 409 TERMS_VERSION_STALE (top level, or under `details`). */
const staleIdsOf = (err: unknown): string[] => {
  if (!(err instanceof ApiError)) return [];
  const raw = err.body?.staleDocumentIds ?? err.details?.staleDocumentIds;
  return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
};

export default function TermsStep() {
  const dispatch = useOnboardingDispatch();
  const navigate = useNavigate();
  const auth = useAuth();
  const { t, lang } = useLanguage();
  const { downloadingDocId, download } = useTermsDownload();

  const [documents, setDocuments] = useState<TermsDocumentPublic[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [agreements, setAgreements] = useState<Map<string, boolean>>(new Map());
  const [reviewedAt, setReviewedAt] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadToast, setDownloadToast] = useState<string | null>(null);
  const docPanelRef = useRef<HTMLDivElement | null>(null);

  // Spec 071 — signed terms agreement. `signing` null = switch state not known yet.
  const [signing, setSigning] = useState<TermsSigningContext | null>(null);
  const [signingFailed, setSigningFailed] = useState(false);
  const [fullName, setFullName] = useState('');
  const [signerRole, setSignerRole] = useState<TermsSignerRole | null>(null);
  const [consent, setConsent] = useState(false);
  const [signatureForceErrors, setSignatureForceErrors] = useState(false);
  // QA F1 — the server refused the name as typed (e.g. a letter the PDF font cannot draw).
  const [serverNameRejected, setServerNameRejected] = useState(false);
  const [staleNotice, setStaleNotice] = useState<string | null>(null);

  const tr = useCallback(
    (key: string, fallback: string) => {
      const v = t(key);
      return v && v !== key ? v : fallback;
    },
    [t],
  );

  const loadSigningContext = useCallback(async () => {
    if (!auth.token) return;
    setSigningFailed(false);
    try {
      setSigning(await api.getTermsSigningContext(auth.token));
    } catch (err) {
      // A backend without spec 071 has no such route: behave exactly as before the feature.
      if (err instanceof ApiError && err.status === 404) {
        setSigning({ enabled: false, consentWording: null, signerEmail: null });
        return;
      }
      setSigning(null);
      setSigningFailed(true);
    }
  }, [auth.token]);

  useEffect(() => {
    void loadSigningContext();
  }, [loadSigningContext]);

  const loadDocuments = async () => {
    setLoadingDocs(true);
    setLoadError(null);
    try {
      const docs = await api.getActiveTermsDocuments();
      const sorted = sortDocs(docs);
      setDocuments(sorted);
      const initial = new Map<string, boolean>();
      sorted.forEach((doc) => initial.set(doc.id, false));
      setAgreements(initial);
      setActiveIndex(0);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load terms documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    void loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!downloadToast) return;
    const id = window.setTimeout(() => setDownloadToast(null), 4000);
    return () => window.clearTimeout(id);
  }, [downloadToast]);

  useEffect(() => {
    if (docPanelRef.current) docPanelRef.current.scrollTop = 0;
  }, [activeIndex]);

  const isArabic = lang === 'ar';
  const totalDocs = documents.length;
  const allAgreed = totalDocs > 0 && documents.every((d) => agreements.get(d.id));
  const isSummaryView = activeIndex >= totalDocs;
  const selectedDoc = isSummaryView ? null : (documents[activeIndex] ?? null);
  const isLastDoc = !isSummaryView && activeIndex === totalDocs - 1;
  const consentWording = signing?.enabled ? signing.consentWording : null;
  const signingEnabled = consentWording !== null;
  const signatureOk =
    !signingEnabled || (isSignatureValid(fullName, signerRole, consent) && !serverNameRejected);
  // Submit stays disabled while the switch state is unknown (context not loaded or failed).
  const canSubmit = allAgreed && !loading && signing !== null && signatureOk;

  const labels = useMemo(
    () => ({
      progress: {
        docOfN: (current: number, total: number, title: string) =>
          interpolate(
            t('onboarding.terms.progress.docOfN') ||
              'Document {current} of {total} — {title}',
            { current, total, title },
          ),
        finalReview: t('onboarding.terms.finalReviewLabel') || 'Final review',
        chipLockedAria: t('onboarding.terms.chip.locked') || 'Locked document',
        chipAgreedAria: t('onboarding.terms.chip.agreed') || 'Agreed document',
        chipCurrentAria: t('onboarding.terms.chip.current') || 'Current document',
      },
      timeline: {
        upNext: t('onboarding.terms.upNext') || 'Up next',
        agreed: t('onboarding.terms.agreed') || 'Agreed',
        finalReview: t('onboarding.terms.finalReviewLabel') || 'Final review',
        unlocksAfter:
          t('onboarding.terms.unlocksAfter') || 'Unlocks after every agreement',
      },
      doc: {
        docEyebrow: (current: number, total: number) =>
          interpolate(t('onboarding.terms.docEyebrow') || 'Step {current} of {total}', {
            current,
            total,
          }),
        download: t('onboarding.terms.download.button') || 'Download',
        downloadInProgress:
          t('onboarding.terms.download.inProgress') || 'Preparing PDF…',
        agreeAndNextOf: (next: number, total: number) =>
          interpolate(
            t('onboarding.terms.cta.agreeAndNextOf') ||
              'I agree — continue to document {next} of {total}',
            { next, total },
          ),
        agreeAndReview:
          t('onboarding.terms.agreeAndReview') ||
          'I agree — review my submission',
        agreeAndContinue:
          t('onboarding.terms.cta.agreeAndContinue') || 'I agree and continue',
        alreadyAgreed:
          t('onboarding.terms.alreadyAgreed') || 'You have agreed to this document.',
        continueNext:
          t('onboarding.terms.continueNext') || 'Continue to next document →',
        goToReview: t('onboarding.terms.goToReview') || 'Go to final review →',
      },
      summary: {
        eyebrow: t('onboarding.terms.summary.lastStepEyebrow') || 'Last step',
        title: t('onboarding.terms.summaryTitle') || 'Ready to submit',
        subtitle:
          t('onboarding.terms.summarySubtitle') ||
          'You have accepted every required agreement. Review the list below and submit your application to finalize your registration.',
        rowAgreed: t('onboarding.terms.agreed') || 'Agreed',
        viewAgain: t('onboarding.terms.viewAgain') || 'View again',
        download: t('onboarding.terms.download.button') || 'Download',
        downloadInProgress:
          t('onboarding.terms.download.inProgress') || 'Preparing PDF…',
        submit: t('onboarding.terms.submit') || 'Submit application',
        submitting: t('onboarding.terms.submitting') || 'Submitting...',
      },
    }),
    [t],
  );

  const handleAgreeAndContinue = () => {
    if (!selectedDoc) return;
    setAgreements((prev) => new Map(prev).set(selectedDoc.id, true));
    setReviewedAt((prev) => new Map(prev).set(selectedDoc.id, new Date().toISOString()));
    if (activeIndex < totalDocs - 1) {
      setActiveIndex(activeIndex + 1);
    } else {
      setActiveIndex(totalDocs); // jump to summary
    }
  };

  const handleContinueNext = () => {
    if (activeIndex < totalDocs - 1) setActiveIndex(activeIndex + 1);
    else setActiveIndex(totalDocs);
  };

  const handleTimelineClick = (index: number) => {
    if (index < totalDocs) {
      const doc = documents[index];
      const isAgreed = agreements.get(doc.id) ?? false;
      const everyPriorAgreed = documents
        .slice(0, index)
        .every((d) => agreements.get(d.id));
      if (isAgreed || index === activeIndex || everyPriorAgreed) {
        setActiveIndex(index);
      }
      return;
    }
    if (allAgreed) setActiveIndex(totalDocs);
  };

  const handleDownload = async (doc: TermsDocumentPublic) => {
    const result = await download(doc, isArabic ? 'ar' : 'en');
    if (result.ok) {
      if (result.reason === 'arabic-unavailable-fallback-english') {
        setDownloadToast(
          t('onboarding.terms.download.toast.fallbackAr') ||
            'Arabic version unavailable — downloaded English copy.',
        );
      } else {
        setDownloadToast(
          interpolate(
            t('onboarding.terms.download.toast.success') ||
              '{fileName} downloaded',
            { fileName: result.fileName },
          ),
        );
      }
    } else {
      setDownloadToast(
        t('onboarding.terms.download.toast.error') ||
          "Couldn't generate the PDF. Please try again.",
      );
    }
  };

  /**
   * Spec 071 AC-4 — the terms changed under the partner: reload them, un-agree the stale ones
   * (agreements on unchanged documents stay), and open the first stale document.
   */
  const reloadAfterStale = async (staleIds: string[]) => {
    let fresh: TermsDocumentPublic[];
    try {
      fresh = sortDocs(await api.getActiveTermsDocuments());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load terms documents');
      return;
    }
    const stale = new Set(staleIds);
    const known = new Set(documents.map((d) => d.id));
    const next = new Map<string, boolean>();
    fresh.forEach((d) =>
      next.set(d.id, known.has(d.id) && !stale.has(d.id) && (agreements.get(d.id) ?? false)),
    );
    setDocuments(fresh);
    setAgreements(next);
    setReviewedAt((prev) => {
      const copy = new Map(prev);
      fresh.forEach((d) => {
        if (!next.get(d.id)) copy.delete(d.id);
      });
      return copy;
    });
    const firstStale = fresh.findIndex((d) => stale.has(d.id));
    const firstOpen = fresh.findIndex((d) => !next.get(d.id));
    setActiveIndex(firstStale >= 0 ? firstStale : firstOpen >= 0 ? firstOpen : fresh.length);
    setStaleNotice(
      tr('onboarding.terms.signature.staleTerms', 'The terms were updated. Please review them again.'),
    );
  };

  const handleSubmit = async () => {
    if (!auth.token || !canSubmit) return;
    setError(null);
    setStaleNotice(null);
    setLoading(true);
    try {
      const agreedTermsDocumentIds = documents.map((doc) => doc.id);
      await api.finalizeDraft(
        consentWording
          ? {
              agreedTermsDocumentIds,
              agreedTermsVersionIds: documents
                .map((doc) => doc.versionId)
                .filter((v): v is string => typeof v === 'string'),
              signature: {
                // Exactly as typed — the server stores the name verbatim (validated trimmed).
                fullName,
                signerRole: signerRole as TermsSignerRole,
                consentAccepted: consent,
                consentWordingVersion: consentWording.version,
                displayLanguage: lang === 'ar' ? 'ar' : 'en',
              },
              acceptTerms: true,
              acceptPartnershipPolicy: true,
            }
          : {
              agreedTermsDocumentIds,
              acceptTerms: true,
              acceptPartnershipPolicy: true,
            },
        auth.token,
      );
      await api.acceptTerms(auth.token);
      await api.acceptPartnershipPolicy(auth.token);
      dispatch({ type: 'RESET' });
      navigate('/onboarding/success', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      const code = err instanceof ApiError ? err.code : null;
      const status = err instanceof ApiError ? err.status : null;
      // Spec 071 AC-4 — a document got a new version while the partner was on the step.
      if (status === 409 && hasCode(err, 'TERMS_VERSION_STALE')) {
        await reloadAfterStale(staleIdsOf(err));
        return;
      }
      // Spec 071 — the switch went ON mid-step: load the wording and show the signature block.
      if (status === 400 && hasCode(err, 'SIGNATURE_REQUIRED')) {
        await loadSigningContext();
        setSignatureForceErrors(true);
        return;
      }
      if (status === 400 && hasCode(err, 'UNKNOWN_CONSENT_WORDING')) {
        await loadSigningContext();
        setConsent(false);
      }
      if (
        status === 400 &&
        apiErrorCodes(err).some((c) => c.startsWith('signature.'))
      ) {
        setSignatureForceErrors(true);
        if (err instanceof ApiError && err.fieldErrors?.['signature.fullName']) setServerNameRejected(true);
      }
      // Spec 051 — finalize 422 VAT_STATUS_NOT_DECLARED: the mandatory VAT question
      // on the first step was never answered (e.g. draft resumed from an older session).
      if (code === 'VAT_STATUS_NOT_DECLARED' || /VAT_STATUS_NOT_DECLARED/i.test(msg)) {
        setError(
          'Please tell us whether your garage is registered for VAT before submitting — go back to the first step (Trade License & ID) and answer the VAT question.',
        );
      } else {
        setError(msg || t('onboarding.terms.submitFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingDocs) {
    return (
      <div>
        <h2 className="wizard-step-title">{t('onboarding.terms.title')}</h2>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        <h2 className="wizard-step-title">{t('onboarding.terms.title')}</h2>
        <div className="error-message">{loadError}</div>
        <div className="wizard-btn-row">
          <button
            className="wizard-btn wizard-btn-secondary"
            onClick={() => navigate('/onboarding/garage-info')}
          >
            {t('common.back')}
          </button>
          <button
            className="wizard-btn wizard-btn-primary"
            onClick={() => void loadDocuments()}
          >
            {t('common.tryAgain') || 'Try again'}
          </button>
        </div>
      </div>
    );
  }

  if (totalDocs === 0) {
    return (
      <div>
        <h2 className="wizard-step-title">{t('onboarding.terms.title')}</h2>
        <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
          {t('onboarding.terms.empty') || 'No terms to review.'}
        </p>
        {error && <div className="error-message">{error}</div>}
        <div className="wizard-btn-row">
          <button
            className="wizard-btn wizard-btn-secondary"
            onClick={() => navigate('/onboarding/garage-info')}
          >
            {t('common.back')}
          </button>
          <button
            className="wizard-btn wizard-btn-primary"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {loading ? t('onboarding.terms.submitting') : t('onboarding.terms.submit')}
          </button>
        </div>
      </div>
    );
  }

  const signatureLabels = {
    title: tr('onboarding.terms.signature.title', 'Sign the agreement'),
    fullName: tr('onboarding.terms.signature.fullName', 'Full name'),
    fullNamePlaceholder: tr('onboarding.terms.signature.fullNamePlaceholder', 'Type your full name'),
    fullNameError: tr('onboarding.terms.signature.fullNameError', 'Type your full name to sign.'),
    fullNameCharset: tr('onboarding.terms.signature.fullNameCharset', 'Use Latin or Arabic letters only.'),
    role: tr('onboarding.terms.signature.role', 'Your role'),
    roleOwner: tr('onboarding.terms.signature.roleOwner', 'Owner'),
    roleAuthorised: tr('onboarding.terms.signature.roleAuthorised', 'Authorised signatory'),
    roleError: tr('onboarding.terms.signature.roleError', 'Choose your role.'),
    consentError: tr('onboarding.terms.signature.consentError', 'Tick the box to accept the terms.'),
    emailNote: signing?.signerEmail
      ? interpolate(
          tr('onboarding.terms.signature.emailNote', 'A signed copy will be emailed to {email}'),
          { email: signing.signerEmail },
        )
      : '',
  };

  const signatureSlot = signingFailed ? (
    <div className="terms-signature-load-error" role="alert">
      <span>
        {tr('onboarding.terms.signature.loadError', "We couldn't load the signing details.")}
      </span>
      <button
        type="button"
        className="terms-summary-edit"
        onClick={() => void loadSigningContext()}
      >
        {tr('onboarding.terms.signature.retry', 'Retry')}
      </button>
    </div>
  ) : consentWording ? (
    <TermsSignatureBlock
      fullName={fullName}
      signerRole={signerRole}
      consent={consent}
      consentText={isArabic ? consentWording.textAr : consentWording.textEn}
      forceErrors={signatureForceErrors}
      serverNameRejected={serverNameRejected}
      disabled={loading}
      onFullNameChange={(value) => {
        setFullName(value);
        setServerNameRejected(false);
      }}
      onSignerRoleChange={setSignerRole}
      onConsentChange={setConsent}
      labels={signatureLabels}
    />
  ) : null;

  // With the signature on, the button carries the server's action label (stored as consent_action).
  const summaryLabels = consentWording
    ? {
        ...labels.summary,
        submit:
          (isArabic ? consentWording.actionLabelAr : consentWording.actionLabelEn) ||
          labels.summary.submit,
      }
    : labels.summary;

  const subtitleText =
    t('onboarding.terms.subtitle') ||
    'Review and accept the agreements below to complete your registration.';

  return (
    <div className="terms-step-container">
      <h2 className="wizard-step-title terms-step-heading">
        {t('onboarding.terms.title')}
      </h2>
      <p className="terms-step-subtitle">{subtitleText}</p>

      {staleNotice && (
        <div className="terms-stale-notice" role="alert">
          {staleNotice}
        </div>
      )}

      <TermsProgressHeader
        documents={documents}
        activeIndex={activeIndex}
        agreements={agreements}
        isSummaryView={isSummaryView}
        isArabic={isArabic}
        onChipClick={handleTimelineClick}
        labels={labels.progress}
      />

      <div className="terms-two-panel terms-two-panel-v2">
        <TermsTimelineRail
          documents={documents}
          activeIndex={activeIndex}
          agreements={agreements}
          isSummaryView={isSummaryView}
          isArabic={isArabic}
          allAgreed={allAgreed}
          onTimelineClick={handleTimelineClick}
          labels={labels.timeline}
        />

        <div
          ref={docPanelRef}
          className={`terms-document-panel${isSummaryView ? ' is-summary' : ''}`}
        >
          {selectedDoc && (
            <TermsDocumentPanel
              doc={selectedDoc}
              activeIndex={activeIndex}
              totalDocs={totalDocs}
              isAgreed={agreements.get(selectedDoc.id) ?? false}
              isLast={isLastDoc}
              isArabic={isArabic}
              isDownloading={downloadingDocId === selectedDoc.id}
              onAgree={handleAgreeAndContinue}
              onContinue={handleContinueNext}
              onDownload={() => void handleDownload(selectedDoc)}
              labels={labels.doc}
            />
          )}

          {isSummaryView && (
            <TermsFinalReview
              documents={documents}
              reviewedAt={reviewedAt}
              isArabic={isArabic}
              isSubmitting={loading}
              canSubmit={canSubmit}
              error={error}
              downloadingDocId={downloadingDocId}
              onViewAgain={(docId) => setActiveIndex(documents.findIndex((d) => d.id === docId))}
              onDownload={(doc) => void handleDownload(doc)}
              onSubmit={handleSubmit}
              signatureSlot={signatureSlot}
              labels={summaryLabels}
            />
          )}
        </div>
      </div>

      {!isSummaryView && error && (
        <div className="error-message" style={{ marginTop: '1rem' }}>
          {error}
        </div>
      )}

      {downloadToast && (
        <div className="terms-toast" role="status" aria-live="polite">
          {downloadToast}
        </div>
      )}

      <div className="wizard-btn-row">
        <button
          className="wizard-btn wizard-btn-secondary"
          onClick={() => navigate('/onboarding/garage-info')}
        >
          {t('common.back')}
        </button>
      </div>
    </div>
  );
}
